import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import TagManager from 'react-gtm-module';
import './AddMyPlaceFlow.scss';
import WizardHeader from './WizardHeader';
import AddressStep from './steps/AddressStep';
import BuildingConfirmStep from './steps/BuildingConfirmStep';
import {
  buildingToListingState,
  capitalizeAddressParts,
} from './buildingToListingState';
import SizeStep from './steps/SizeStep';
import RoomsStep from './steps/RoomsStep';
import ReadyToPublishStep from './steps/ReadyToPublishStep';
import ShareListingStep from './steps/ShareListingStep';
import FlowMap from './FlowMap';
import { BuildingWithConvertedAddresses, LatLng } from '../../../utils/types';
import { useBodyScrollLock } from '../../../../hooks/useBodyScrollLock';
import { useDialogFocus } from '../../../common/useDialogFocus';
import useListingImageUpload, {
  ListingImageData,
} from '../../../../hooks/useListingImageUpload';
import { getCookie } from '../../../utils/utils';
import { startListingPriceEstimation } from '../../../utils/startListingPriceEstimation';
import { useFlowPresenceSignal } from '../useFlowPresenceSignal';
import { fetchPOIsForBuilding } from '../../LandingPage/LandingPage.helpers';
import { saveEmptyBuildingPage } from '../../../BuildingModal/Report.fetchExistingReport';
import { ImageLightbox } from '../../../ImageLightbox/ImageLightbox';
import {
  resolveListingBuilding,
  ListingAddressObject,
  ListingBuildingGeometry,
} from './resolveListingBuilding';
import {
  AddMyPlaceStep,
  AddMyPlaceFormState,
  BuildingTypeValue,
  createInitialFormState,
  MAX_PROPERTY_DESCRIPTION_LENGTH,
} from './types';
import { localStore, sessionStore } from '../../../utils/safeStorage';

export interface AddMyPlaceSummary {
  address: string;
  area: string;
  size: string;
  rooms: string;
  // True when buyers already hold a buying interest in this building. No chat
  // is created for them (alert-based matching); the seller sees them under
  // Activity and reaches out via the suggest flow.
  isMatch?: boolean;
  // The created listing's building id + centroid, so the landing page can open
  // the real on-map building preview card for it once the flow completes.
  buildingId?: string;
  coordinates?: LatLng;
  // True when the flow was launched from an already-open building view ("Sell
  // here"). The landing page then skips opening the on-map preview card, since
  // the full building view is already on screen.
  launchedFromBuildingView?: boolean;
}

// Pre-known building + address handed to the flow when launched from the
// building view's "Sell here" CTA. The flow opens on the building step with
// these values already filled, so the user only confirms (or changes the
// building / building type) before continuing.
export interface AddMyPlaceSeed {
  buildingId: string;
  // Street + house number (e.g. "Bulevardi 9").
  address: string;
  // City (e.g. "Helsinki").
  area: string;
  // Postcode (e.g. "00120").
  postal: string;
  // Building centroid the listing is created at.
  center: LatLng;
  addressObject: ListingAddressObject;
  geometry: ListingBuildingGeometry | null;
}

// The real off-market willingness created on publish, threaded to the share
// step so its Share button can share the actual listing link.
export interface CreatedListingData {
  eventId: string;
  buildingId: string;
  address: ListingAddressObject;
  isMatch: boolean;
}

interface AddMyPlaceFlowProps {
  isOpen: boolean;
  isLoggedIn: boolean;
  // Current user's email, threaded to the share step for the best-effort boost
  // request.
  userEmail?: string;
  onClose: () => void;
  onComplete: (summary: AddMyPlaceSummary) => void;
  // Seeded into the flow maps so they open on the same spot the user is
  // currently viewing on the landing map.
  mapCenter: LatLng;
  mapZoom: number;
  countryOfFeedContent: string;
  // Opens the shared signup/login modal without unmounting this flow, preserving
  // the user's in-progress listing details while they authenticate.
  setLoginModalVisible: (show: boolean) => void;
  // Surfaces a publish failure on the landing page's toast. Required rather than
  // optional: the toast is the only place these errors are shown, so a missing
  // handler would swallow them silently.
  onToast: (message: string) => void;
  // Keeps background map listings/counts paused through the close animation.
  onPresenceChange?: (isPresent: boolean) => void;
  // The expanded landing map already pins and clips the page. Re-fixing the
  // body around this flow makes Mapbox clear its canvas for one frame.
  lockBodyScroll: boolean;
  // When set, the flow opens on the building step pre-filled with this building
  // (launched from the building view's "Sell here"). Null/undefined starts the
  // normal address-first flow.
  seed?: AddMyPlaceSeed | null;
}

// Duration of the open/close transition; kept in sync with the longest CSS
// transition on `.amp-flow` so the overlay unmounts only after it slides out.
const ANIMATION_MS = 600;
export const AMP_RESTORE_STORAGE_KEY = 'add_my_place_flow_restore';
export const AMP_PAYMENT_QUERY_PARAM = 'amp-payment';
const AMP_PHOTO_LIGHTBOX_SEEN_KEY = 'add_my_place_photo_lightbox_seen';

// Centered pin on the address-step map; geo-anchored house on the building-step
// callout. Both belong to the single shared FlowMap owned by this flow.
const ADDRESS_PIN_IMG = `${process.env.PUBLIC_URL}/assets/Pin.png`;
const BUILDING_CALLOUT_IMG = `${process.env.PUBLIC_URL}/assets/markers/seller/Home.png`;

export type ClickedBuildingState = {
  buildingId: string;
  addressObject: ListingAddressObject;
  geometry: ListingBuildingGeometry | null;
} | null;

// Exported so the estimate flow (JAA-285) can hand off its collected state
// through the same localStorage restore path the Stripe return uses.
export interface AddMyPlaceRestoreState {
  step: AddMyPlaceStep;
  form: AddMyPlaceFormState;
  selectedCenter: LatLng | null;
  listingLocation: LatLng | null;
  clickedBuilding: ClickedBuildingState;
  requiresManualBuildingSelection: boolean;
  isDetailsExpanded: boolean;
  images: ListingImageData[];
  // Whether this session began from the building view's "Sell here" (seed), so
  // the post-Stripe return path can restore it and still close correctly
  // instead of reopening the on-map preview card.
  enteredFromBuildingView: boolean;
  savedAt: number;
}

// Maps each step to its dot-progress index (null hides the dots).
const STEP_DOT: Partial<Record<AddMyPlaceStep, number | null>> = {
  address: 0,
  building: 0,
  size: 1,
  rooms: 2,
  publish: null,
  share: null,
};

/**
 * Full-screen sell-listing flow: pick the building, describe and photograph the
 * home, publish, then share.
 */
const AddMyPlaceFlow = ({
  isOpen,
  isLoggedIn,
  userEmail,
  onClose,
  onComplete,
  mapCenter,
  mapZoom,
  countryOfFeedContent,
  setLoginModalVisible,
  onToast,
  onPresenceChange,
  lockBodyScroll,
  seed,
}: AddMyPlaceFlowProps) => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState<AddMyPlaceStep>('address');
  const [form, setForm] = useState<AddMyPlaceFormState>(createInitialFormState);
  // Captured when the user picks an address suggestion, so the building step
  // recenters on the chosen place rather than the initial landing centroid.
  const [selectedCenter, setSelectedCenter] = useState<LatLng | null>(null);
  // Precise coordinate the real listing is created at. Set from the picked
  // address and refined when the user confirms a specific building on the map.
  const [listingLocation, setListingLocation] = useState<LatLng | null>(null);
  // Structured address + footprint captured when the user clicks a building in
  // the building step. Preferred over a fresh resolve at publish time because
  // it's geocoded at the exact confirmed building.
  const [clickedBuilding, setClickedBuilding] =
    useState<ClickedBuildingState>(null);
  // True when the picked address was precise, but Mapbox did not have an
  // automatic building match there. The building step must wait for a map click.
  const [requiresManualBuildingSelection, setRequiresManualBuildingSelection] =
    useState(false);
  // A clicked building is still resolving, so the previous selection must not show or advance.
  const [isBuildingPickPending, setIsBuildingPickPending] = useState(false);
  // The clicked building had no address or its lookup failed: say so, never fall back to the previous one.
  const [isBuildingPickFailed, setIsBuildingPickFailed] = useState(false);
  // Pick generation, bumped each time the building step opens or closes (JAA-410).
  const pickGenerationRef = useRef(0);
  // Generation of the map pick whose lookup is in flight; null when none is.
  const pendingPickRef = useRef<number | null>(null);
  // True while the publish POST is in flight (drives the CTA loading state).
  const [isPublishing, setIsPublishing] = useState(false);
  // "Add more details" expanded state of the publish step. Held here so the
  // desktop modal can grow to full height while the details are open.
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  // Lifted from AddressStep so the shared map (now a sibling of the step, not a
  // descendant) can still fade out while the user types/focuses the input.
  const [isAddressFocused, setIsAddressFocused] = useState(false);
  // The real willingness created on publish, threaded to the share step.
  const [createdListing, setCreatedListing] =
    useState<CreatedListingData | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [hasSeenPhotoLightbox, setHasSeenPhotoLightbox] = useState(
    () => localStore.getItem(AMP_PHOTO_LIGHTBOX_SEEN_KEY) === '1',
  );
  // `isMounted` keeps the overlay in the DOM through the close animation;
  // `isActive` drives the `data-open` flag that triggers the slide/fade.
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isActive, setIsActive] = useState(false);
  const restoreStateRef = useRef<AddMyPlaceRestoreState | null>(null);
  // True when this open started from the building view's "Sell here" (seed),
  // so the building step is the entry point: Back closes the flow (no address
  // step), and completion skips re-opening the on-map preview card.
  const enteredFromBuildingViewRef = useRef(false);
  const clearSavedRestore = useCallback(() => {
    localStore.removeItem(AMP_RESTORE_STORAGE_KEY);
  }, []);
  const handleImageCreditsRequired = useCallback(async () => {
    try {
      const restoreState = restoreStateRef.current;
      if (restoreState) {
        localStore.setItemOrThrow(
          AMP_RESTORE_STORAGE_KEY,
          JSON.stringify({
            ...restoreState,
            step: 'publish',
            savedAt: Date.now(),
          }),
        );
      }

      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set(AMP_PAYMENT_QUERY_PARAM, 'credits');
      const returnUrlPath = `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
      localStore.setItemOrThrow('stripe_return_url', returnUrlPath);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/payments/create-checkout-session`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: JSON.stringify({ return_url: returnUrl.toString() }),
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to create checkout session with status ${response.status}`,
        );
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Error purchasing AMP image credits:', error);
      window.alert(t('building.offMarket.modal.purchaseError'));
    }
  }, [t]);
  const {
    images,
    uploadError,
    isUploading: isUploadingImages,
    isRegenerating,
    enhancementStatus,
    fetchEnhancementStatus,
    uploadFiles,
    removeImage,
    reorderImages,
    toggleImageVersion,
    regenerateImage,
    resetImages,
    restoreImages,
  } = useListingImageUpload({ onCreditsRequired: handleImageCreditsRequired });

  // Pin document scroll through the close animation when the page behind this
  // flow is scrollable. The expanded landing map is already pinned and opts
  // out, avoiding a Mapbox canvas resize while the flow opens or closes.
  useBodyScrollLock(isMounted && lockBodyScroll);
  useFlowPresenceSignal(isMounted, onPresenceChange);
  // Focus moves into the dialog on open, Tab stays inside it, and the opener
  // gets focus back on close — the part aria-modal cannot do on its own.
  const dialogRef = useDialogFocus<HTMLDivElement>(isMounted && isOpen);

  // Fresh state every time the flow is (re)opened.
  useEffect(() => {
    if (isOpen) {
      const savedRestore = localStore.getItem(AMP_RESTORE_STORAGE_KEY);
      if (savedRestore) {
        try {
          const parsed = JSON.parse(
            savedRestore,
          ) as Partial<AddMyPlaceRestoreState>;
          setStep(parsed.step || 'publish');
          setForm(parsed.form || createInitialFormState());
          setSelectedCenter(parsed.selectedCenter ?? null);
          setListingLocation(parsed.listingLocation ?? null);
          setClickedBuilding(parsed.clickedBuilding ?? null);
          setRequiresManualBuildingSelection(
            Boolean(parsed.requiresManualBuildingSelection),
          );
          enteredFromBuildingViewRef.current = Boolean(
            parsed.enteredFromBuildingView,
          );
          setCreatedListing(null);
          setLightboxIndex(null);
          restoreImages(Array.isArray(parsed.images) ? parsed.images : []);
          setIsPublishing(false);
          setIsDetailsExpanded(Boolean(parsed.isDetailsExpanded));
          clearSavedRestore();
          return;
        } catch (error) {
          console.error('Failed to restore AMP flow after payment:', error);
          clearSavedRestore();
        }
      }

      // Launched from the building view's "Sell here": open on the building
      // step with the known building + address already filled in.
      if (seed) {
        enteredFromBuildingViewRef.current = true;
        setStep('building');
        setForm({
          ...createInitialFormState(),
          address: seed.address,
          area: seed.area,
          postal: seed.postal,
        });
        setSelectedCenter(seed.center);
        setListingLocation(seed.center);
        setClickedBuilding({
          buildingId: seed.buildingId,
          addressObject: seed.addressObject,
          geometry: seed.geometry,
        });
        setRequiresManualBuildingSelection(false);
        setCreatedListing(null);
        setLightboxIndex(null);
        resetImages();
        setIsPublishing(false);
        setIsDetailsExpanded(false);
        return;
      }

      enteredFromBuildingViewRef.current = false;
      setStep('address');
      setForm(createInitialFormState());
      setSelectedCenter(null);
      setListingLocation(null);
      setClickedBuilding(null);
      setRequiresManualBuildingSelection(false);
      setCreatedListing(null);
      setLightboxIndex(null);
      resetImages();
      setIsPublishing(false);
      setIsDetailsExpanded(false);
    }
  }, [isOpen, seed, clearSavedRestore, resetImages, restoreImages]);

  // Refresh remaining enhancement credits whenever the flow opens for a logged-in
  // user, so the lightbox can show the up-to-date "X left" count (also after a
  // Stripe credit purchase reopens the flow).
  useEffect(() => {
    if (isOpen && isLoggedIn) {
      fetchEnhancementStatus();
    }
  }, [isOpen, isLoggedIn, fetchEnhancementStatus]);

  useEffect(() => {
    restoreStateRef.current = {
      step,
      form,
      selectedCenter,
      listingLocation,
      clickedBuilding,
      requiresManualBuildingSelection,
      isDetailsExpanded,
      images: images
        .filter(
          (image) =>
            !image.isUploading &&
            !image.error &&
            !image.url?.startsWith('blob:'),
        )
        .map((image) => ({
          ...image,
          isUploading: false,
          isEnhancing: false,
        })),
      enteredFromBuildingView: enteredFromBuildingViewRef.current,
      savedAt: Date.now(),
    };
  }, [
    step,
    form,
    selectedCenter,
    listingLocation,
    clickedBuilding,
    requiresManualBuildingSelection,
    isDetailsExpanded,
    images,
  ]);

  // Collapse the details whenever the user leaves the publish step, matching
  // the pre-hoist behavior where the step remounted with details closed.
  useEffect(() => {
    if (step !== 'publish') setIsDetailsExpanded(false);
  }, [step]);

  // Clear the address-focused flag when leaving the address step so the shared
  // map isn't left faded when returning to it later.
  useEffect(() => {
    if (step !== 'address') setIsAddressFocused(false);
  }, [step]);

  // Mount on open; on close, clear the active flag to animate out and unmount
  // only after the transition finishes.
  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      return undefined;
    }
    setIsActive(false);
    const timer = setTimeout(() => setIsMounted(false), ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Activate only AFTER the closed state has been committed/painted, so the
  // enter transition runs from translateY(100%) -> translateY(0). Scheduling
  // this in its own effect (keyed on isMounted) avoids the mount/activate race
  // that otherwise collapses both into one commit and skips the animation.
  useEffect(() => {
    if (!isMounted || !isOpen) return undefined;
    const raf = requestAnimationFrame(() => setIsActive(true));
    return () => cancelAnimationFrame(raf);
  }, [isMounted, isOpen]);

  const updateForm = useCallback((updates: Partial<AddMyPlaceFormState>) => {
    setForm((prev) => ({ ...prev, ...updates }));
  }, []);

  const effectiveCenter = selectedCenter ?? mapCenter;

  /**
   * Building step: apply the building the map resolved to the flow's address,
   * listing point and clicked building (it does NOT open the building modal).
   * Only the pick in flight in the current visit to the step applies. A result
   * from an earlier visit (the user went Back, typed another or the same
   * address, or closed and reopened the flow) or one that arrives after the
   * pick settled is ignored, so a late lookup never rewrites an address the
   * user has moved on from (JAA-410).
   */
  const handleSelectBuilding = useCallback(
    (building: BuildingWithConvertedAddresses) => {
      if (pendingPickRef.current !== pickGenerationRef.current) return;
      pendingPickRef.current = null;
      const selection = buildingToListingState(building);
      updateForm(selection.formPatch);
      if (selection.listingLocation)
        setListingLocation(selection.listingLocation);
      if (selection.clickedBuilding)
        setClickedBuilding(selection.clickedBuilding);
      setIsBuildingPickPending(false);
    },
    [updateForm],
  );

  /**
   * A building-step map click started resolving another building. The pick is
   * recorded here, synchronously, so a cached lookup that resolves before the
   * next render is still accepted; it also clears an earlier failed pick.
   */
  const handleBuildingPickStart = useCallback(() => {
    pendingPickRef.current = pickGenerationRef.current;
    setIsBuildingPickPending(true);
    setIsBuildingPickFailed(false);
  }, []);

  /**
   * The pick in flight found no address for the clicked building, or its
   * lookup failed. The step says so and keeps Next disabled; it does not fall
   * back to the previous building's address while the new building is
   * highlighted (JAA-410). A failure from an earlier pick is ignored.
   */
  const handleBuildingPickFailed = useCallback(() => {
    if (pendingPickRef.current !== pickGenerationRef.current) return;
    pendingPickRef.current = null;
    setIsBuildingPickPending(false);
    setIsBuildingPickFailed(true);
  }, []);

  const addressParts = form.address
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const displayAddress = addressParts[0] || '';
  const displayArea = form.area.trim();
  const selectedAddressCity =
    addressParts
      .slice(1)
      .map((part) => part.replace(/^\d+\s*/, '').trim())
      .find((part) => part && !/^(finland|suomi)$/i.test(part)) || '';
  const previewCity = displayArea || selectedAddressCity;
  const displayPostal = form.postal.trim();
  // Street + "postal city" (postal before the city), used only in the building
  // step's address line and the publish step's address detail field. Other
  // address displays keep the street + city form.
  const cityWithPostal = [displayPostal, previewCity].filter(Boolean).join(' ');
  const addressWithPostal = [displayAddress, cityWithPostal]
    .filter(Boolean)
    .join(', ');
  const displaySize = form.size.trim();
  const displayRooms = form.rooms.trim();

  // Light up the current step's progress dot (dot 0) as fully filled once the
  // building is confirmed (auto-matched or clicked); while manual selection is
  // still pending it stays the active ring, mirroring the address step.
  const isBuildingConfirmed =
    !isBuildingPickPending &&
    !isBuildingPickFailed &&
    (!requiresManualBuildingSelection || clickedBuilding !== null);
  const isBuildingStepOpen = isOpen && step === 'building';
  // While a pick is unresolved or failed, nothing shows the previous building's address.
  const hidePickedAddress = isBuildingPickPending || isBuildingPickFailed;

  // Opening or leaving the building step, or a new seed, starts a new pick generation, so no earlier lookup can land.
  useEffect(() => {
    pickGenerationRef.current += 1;
    pendingPickRef.current = null;
    setIsBuildingPickPending(false);
    setIsBuildingPickFailed(false);
  }, [isBuildingStepOpen, seed]);

  // Light up the current step's progress dot as soon as its input is satisfied:
  // a valid (positive) size on the size step, or a selected count on the rooms
  // step.
  const isSizeValid = displaySize !== '' && Number(displaySize) > 0;
  const isRoomsSelected = displayRooms !== '';
  const activeDotDone =
    (step === 'building' && isBuildingConfirmed) ||
    (step === 'size' && isSizeValid) ||
    (step === 'rooms' && isRoomsSelected);

  const hasGeometryCoordinates = (
    geometry: ListingBuildingGeometry | null | undefined,
  ): geometry is ListingBuildingGeometry =>
    Array.isArray(geometry?.coordinates) && geometry.coordinates.length > 0;

  const handleCloseFlow = useCallback(() => {
    clearSavedRestore();
    onClose();
  }, [clearSavedRestore, onClose]);

  const handleComplete = useCallback(() => {
    onComplete({
      address: displayAddress,
      area: displayArea,
      size: displaySize,
      rooms: displayRooms,
      isMatch: createdListing?.isMatch ?? false,
      buildingId: createdListing?.buildingId,
      coordinates: listingLocation ?? selectedCenter ?? undefined,
      launchedFromBuildingView: enteredFromBuildingViewRef.current,
    });
    handleCloseFlow();
  }, [
    onComplete,
    handleCloseFlow,
    displayAddress,
    displayArea,
    displaySize,
    displayRooms,
    createdListing,
    listingLocation,
    selectedCenter,
  ]);

  const handleOpenPhotoLightbox = useCallback((photoIndex: number) => {
    localStore.setItem(AMP_PHOTO_LIGHTBOX_SEEN_KEY, '1');
    setHasSeenPhotoLightbox(true);
    setLightboxIndex(photoIndex);
  }, []);

  /**
   * Create the real off-market sell listing, then advance to the share step.
   * Mirrors the logged-in `onConfirm` path of the old could-sell flow. Pushes
   * the old flow's GTM sell submit event, which the seller ad conversion in Tag
   * Manager listens to, but only once the listing is saved and once per open:
   * the old flow pushed on click before the request, and a failed save would
   * burn the conversion tag's one-time browser flag. The entry is read before
   * any await so a close-and-reopen mid-publish cannot rename the event.
   */
  const submitListing = async () => {
    if (isUploadingImages) return;
    const fromBuilding = enteredFromBuildingViewRef.current;

    const location = listingLocation ?? selectedCenter;
    if (!location) {
      window.alert(t('building.offMarket.modal.pleaseSelectAddress'));
      return;
    }

    setIsPublishing(true);
    try {
      // Prefer the building data captured at click time. Only boot the hidden
      // Mapbox resolver when the user did not explicitly click a building.
      const resolved = clickedBuilding?.buildingId
        ? null
        : await resolveListingBuilding(location);
      const addressObject =
        clickedBuilding?.addressObject ?? resolved?.addressObject;
      if (!addressObject) {
        window.alert(t('building.offMarket.modal.invalidAddress'));
        return;
      }

      const buildingId =
        clickedBuilding?.buildingId ||
        resolved?.buildingId ||
        `temp_${Date.now()}`;
      const pointGeometry: ListingBuildingGeometry = {
        type: 'Point',
        coordinates: [location.lng, location.lat],
      };
      const geometry = hasGeometryCoordinates(clickedBuilding?.geometry)
        ? clickedBuilding.geometry
        : (resolved?.geometry ?? pointGeometry);
      const propertyAddress = [displayAddress, previewCity]
        .filter(Boolean)
        .join(', ');

      const requestBody: Record<string, unknown> = {
        could_sell: true,
        selected_building: {
          id: buildingId,
          centroid: location,
          geometry,
          addresses: [addressObject],
          properties: {
            name: propertyAddress,
            addresses: [addressObject],
            type: 'apartments',
          },
        },
        property_address: propertyAddress,
        property_location: location,
        property_building_type: form.buildingType,
        user_agent: navigator.userAgent,
        source: 'add_my_place',
      };

      const trimmedDescription = form.description
        .trim()
        .slice(0, MAX_PROPERTY_DESCRIPTION_LENGTH);
      if (trimmedDescription !== '') {
        requestBody.property_description = trimmedDescription;
      }
      if (displaySize !== '') {
        const sizeNum = Number(displaySize);
        if (!Number.isNaN(sizeNum) && sizeNum > 0)
          requestBody.property_size = sizeNum;
      }
      if (displayRooms !== '') {
        requestBody.property_rooms = displayRooms;
      }
      if (form.floor.trim() !== '') {
        const floorNum = Number(form.floor);
        if (!Number.isNaN(floorNum)) requestBody.property_floor = floorNum;
      }
      if (form.hasElevator !== null)
        requestBody.property_has_elevator = form.hasElevator;
      if (form.hasBalcony !== null)
        requestBody.property_has_balcony = form.hasBalcony;
      if (form.renovationStatus !== '') {
        requestBody.property_renovation_status = form.renovationStatus;
      }
      if (form.askingPrice.trim() !== '') {
        const priceNum = Number(form.askingPrice);
        if (!Number.isNaN(priceNum))
          requestBody.property_asking_price = priceNum;
      }
      const validImages = images.filter(
        (image) =>
          image.id &&
          image.url &&
          image.thumbnailUrl &&
          !image.error &&
          !image.isUploading,
      );
      if (validImages.length > 0) {
        requestBody.property_images = validImages.map((image) => ({
          id: image.id,
          url: image.url,
          thumbnailUrl: image.thumbnailUrl,
          originalUrl: image.originalUrl,
          originalThumbnailUrl: image.originalThumbnailUrl,
          enhancedUrl: image.enhancedUrl,
          enhancedThumbnailUrl: image.enhancedThumbnailUrl,
          isDisplayOptimized: image.isDisplayOptimized,
        }));
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/off-market-willingness`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorCode =
          errorData &&
          typeof errorData === 'object' &&
          'error_code' in errorData
            ? (errorData as { error_code?: string }).error_code
            : undefined;
        // Publish failures the user can act on get their own copy; everything
        // else falls back to the generic submit error.
        const publishErrorMessage = () => {
          if (errorCode === 'LIMIT_REACHED') {
            return t('building.offMarket.modal.sellListingsLimitReached');
          }
          if (errorCode === 'ALREADY_SELLING_IN_BUILDING') {
            return t('building.offMarket.modal.alreadySellingInBuilding');
          }
          return t('landingPage.couldBuyOrCouldSellModal.submitError');
        };
        onToast(publishErrorMessage());
        return;
      }

      const responseData = await response.json().catch(() => ({}));

      // Edit from the share step re-publishes; only the first save per open counts.
      if (!createdListing) {
        TagManager.dataLayer({
          dataLayer: {
            event: fromBuilding
              ? 'sell_submit_building_modal'
              : 'sell_submit_landing_page',
            category: 'User Interaction',
            action: 'Click',
            label: fromBuilding
              ? 'Sell Submit Building Modal'
              : 'Sell Submit Landing Page',
          },
        });
      }

      // Best-effort: generate the building page so it exists when opened later
      // (mirrors the old could-sell flow).
      try {
        const buildingWithPOIs = await fetchPOIsForBuilding(
          {
            id: buildingId,
            centroid: location,
            geometry,
            addresses: [addressObject],
            properties: { name: propertyAddress, addresses: [addressObject] },
            pois: [],
          } as unknown as BuildingWithConvertedAddresses,
          location,
        );
        await saveEmptyBuildingPage({ selectedBuilding: buildingWithPOIs });
      } catch (error) {
        console.error(
          'Failed to save empty building page for AMP listing:',
          error,
        );
      }

      const isMatch = Boolean(
        responseData.is_new_could_sell_and_buyers ||
          responseData.is_new_could_buy_and_sellers,
      );

      if (responseData.willingness_id) {
        setCreatedListing({
          eventId: responseData.willingness_id,
          buildingId: buildingId || responseData.building_id || '',
          address: addressObject,
          isMatch,
        });

        // Kick off the automated Jaakko price estimate in the background for the
        // new sell listing (always, even when a custom price was set). Fire-and-
        // forget so it never blocks advancing to the share step. Once it
        // registers (the backend stamps the listing's estimate_status=pending),
        // refresh the map listings + open preview again so the side pane and the
        // on-map preview card show "estimating price" immediately after the
        // share step closes, instead of a stale "No set price". The first
        // refresh below races the estimate POST and would otherwise miss it.
        // Every successful publish is a first publish — the backend refuses a
        // second one on a building you already sell in — so the capability is
        // always there and the estimate is always the automated, listing-bound
        // one. No capability means something went wrong upstream, and a plain
        // estimate would be the wrong kind, so skip rather than guess.
        if (responseData.price_estimation_capability) {
          void startListingPriceEstimation({
            willingnessId: responseData.willingness_id,
            buildingId: buildingId || responseData.building_id || '',
            automationCapability: responseData.price_estimation_capability,
            address: addressObject,
            location,
            propertyDetails: {
              description: form.description.trim(),
              size: form.size.trim() !== '' ? Number(form.size) : null,
              rooms: form.rooms || '',
              floor: form.floor.trim() !== '' ? Number(form.floor) : null,
              hasElevator: form.hasElevator ?? false,
              hasBalcony: form.hasBalcony ?? false,
              renovationStatus: form.renovationStatus || '',
              buildingType: form.buildingType || '',
            },
            language: i18n.language,
          }).then((result) => {
            if (result === 'daily_limit') {
              onToast(
                t('building.offMarket.modal.automaticEstimateDailyLimit'),
              );
            }
            if (result !== 'started') return;
            window.dispatchEvent(new Event('jaakko:listings-changed'));
          });
        }
      }

      // Refresh the explore map's listing layer so the new listing shows immediately.
      window.dispatchEvent(new Event('jaakko:listings-changed'));

      clearSavedRestore();
      setStep('share');
    } catch (error) {
      console.error('Error creating AMP listing:', error);
      onToast(t('landingPage.couldBuyOrCouldSellModal.submitError'));
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePublish = () => {
    if (isPublishing || isUploadingImages) return;
    if (isLoggedIn) {
      submitListing();
    } else {
      // JAA-226 parity: defer marketing consent for the rest of this browser
      // session so it doesn't interrupt the user when they return to publish.
      sessionStore.setItem('marketing_consent_deferred', '1');
      setLoginModalVisible(true);
    }
  };

  const handleBack = useCallback(() => {
    switch (step) {
      case 'building':
        // When the flow was entered on the building step (from "Sell here"),
        // there is no address step behind it — Back closes the flow instead.
        if (enteredFromBuildingViewRef.current) {
          handleCloseFlow();
        } else {
          setStep('address');
        }
        break;
      case 'size':
        setStep('building');
        break;
      case 'rooms':
        setStep('size');
        break;
      case 'publish':
        setStep('rooms');
        break;
      case 'share':
        setStep('publish');
        break;
      case 'address':
      default:
        handleCloseFlow();
        break;
    }
  }, [step, handleCloseFlow]);

  const isPhotoLightboxOpen = lightboxIndex !== null && images.length > 0;

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      // The photo lightbox has its own Escape handler; let it close first so a
      // single Escape doesn't also tear down the whole flow.
      if (isPhotoLightboxOpen) return;
      if (event.key === 'Escape') handleCloseFlow();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleCloseFlow, isPhotoLightboxOpen]);

  if (!isMounted) return null;

  const handleBuildingTypeChange = (value: BuildingTypeValue) =>
    updateForm({ buildingType: value });

  let stepNode: React.ReactNode = null;
  switch (step) {
    case 'address':
      stepNode = (
        <AddressStep
          value={form.address}
          onChange={(value) => updateForm({ address: value })}
          onSelect={(
            value,
            center,
            postal,
            needsManualBuildingSelection = false,
          ) => {
            updateForm({
              address: capitalizeAddressParts(value),
              postal: postal ?? '',
            });
            if (center) {
              setSelectedCenter(center);
              setListingLocation(center);
            }
            // A freshly picked address invalidates any building captured from a
            // previous building-step selection.
            setClickedBuilding(null);
            setRequiresManualBuildingSelection(needsManualBuildingSelection);
            setStep('building');
          }}
          onFocusedChange={setIsAddressFocused}
          searchCenter={effectiveCenter}
        />
      );
      break;
    case 'building':
      stepNode = (
        <BuildingConfirmStep
          addressWithPostal={hidePickedAddress ? '' : addressWithPostal}
          buildingType={form.buildingType}
          onBuildingTypeChange={handleBuildingTypeChange}
          onNext={() => setStep('size')}
          requiresManualBuildingSelection={requiresManualBuildingSelection}
          hasSelectedBuilding={clickedBuilding !== null}
          isSelectionPending={isBuildingPickPending}
          isSelectionFailed={isBuildingPickFailed}
        />
      );
      break;
    case 'size':
      stepNode = (
        <SizeStep
          address={displayAddress}
          area={previewCity}
          size={form.size}
          onSizeChange={(value) => updateForm({ size: value })}
          onNext={() => setStep('rooms')}
        />
      );
      break;
    case 'rooms':
      stepNode = (
        <RoomsStep
          address={displayAddress}
          area={previewCity}
          size={displaySize}
          rooms={form.rooms}
          onSelectRooms={(value) => {
            updateForm({ rooms: value });
          }}
          onNext={() => setStep('publish')}
        />
      );
      break;
    case 'publish':
      stepNode = (
        <ReadyToPublishStep
          form={form}
          displayAddress={displayAddress}
          previewCity={previewCity}
          addressWithPostal={addressWithPostal}
          displaySize={displaySize}
          displayRooms={displayRooms}
          onBuildingTypeChange={handleBuildingTypeChange}
          onFormChange={updateForm}
          onPublish={handlePublish}
          isPublishing={isPublishing}
          detailsExpanded={isDetailsExpanded}
          onDetailsExpandedChange={setIsDetailsExpanded}
          photos={images}
          uploadError={uploadError}
          onUploadPhotos={uploadFiles}
          onRemovePhoto={removeImage}
          onReorderPhotos={reorderImages}
          onOpenPhotoManager={handleOpenPhotoLightbox}
          hasSeenPhotoLightbox={hasSeenPhotoLightbox}
          isLoggedIn={isLoggedIn}
          setLoginModalVisible={setLoginModalVisible}
        />
      );
      break;
    case 'share':
      stepNode = (
        <ShareListingStep
          form={form}
          displayAddress={displayAddress}
          previewCity={previewCity}
          displaySize={displaySize}
          displayRooms={displayRooms}
          photos={images}
          userEmail={userEmail}
          createdListing={createdListing}
          onEdit={() => setStep('publish')}
          onDone={handleComplete}
        />
      );
      break;
    default:
      stepNode = null;
  }

  const isPublishStep = step === 'publish';
  const isShareStep = step === 'share';
  const limitedHeightSteps = ['share', 'address', 'building', 'size', 'rooms'];
  const isLimitedHeightStep = limitedHeightSteps.includes(step);

  // Single shared FlowMap for the address + building steps so moving between
  // them keeps one WebGL context alive (no teardown/rebuild) and the building
  // recenters instantly (jumpTo) with no slow fly animation.
  const isBuildingStep = step === 'building';
  const showFlowMap = step === 'address' || isBuildingStep;
  const selectedBuildingLocation = clickedBuilding ? listingLocation : null;
  const buildingHighlightLocation = requiresManualBuildingSelection
    ? selectedBuildingLocation
    : effectiveCenter;
  const showBuildingCallout = buildingHighlightLocation !== null;
  const sharedFlowMap = showFlowMap ? (
    <FlowMap
      key="amp-shared-map"
      mapCenter={effectiveCenter}
      mapZoom={mapZoom}
      countryOfFeedContent={countryOfFeedContent}
      recenterInstant
      disableBuildingClick={!isBuildingStep}
      staticMap={!isBuildingStep}
      hideLabels={!isBuildingStep}
      selectBuilding={isBuildingStep ? handleSelectBuilding : undefined}
      onBuildingPickStart={isBuildingStep ? handleBuildingPickStart : undefined}
      onBuildingPickFailed={isBuildingStep ? handleBuildingPickFailed : undefined}
      highlightAddressAt={isBuildingStep ? buildingHighlightLocation : null}
      addressCallout={
        isBuildingStep && showBuildingCallout ? (
          <div className="amp-building__callout">
            <img
              src={BUILDING_CALLOUT_IMG}
              alt=""
              aria-hidden="true"
              className="amp-building__callout-house"
            />
            <div className="amp-building__callout-text">
              <span className="amp-building__callout-title">
                {t('newLandingPage.addMyPlace.building.calloutTitle')}
              </span>
              <span className="amp-building__callout-address">
                {hidePickedAddress ? '' : displayAddress}
              </span>
            </div>
          </div>
        ) : undefined
      }
      centerOverlay={
        !isBuildingStep ? (
          <div className="amp-map-pin">
            <img
              src={ADDRESS_PIN_IMG}
              alt=""
              aria-hidden="true"
              className="amp-map-pin__house"
            />
          </div>
        ) : undefined
      }
    />
  ) : null;

  const overlay = (
    <div className="amp-flow__overlay" data-open={isActive ? 'true' : 'false'}>
      <div
        className={`amp-flow${isPublishStep ? ' amp-flow--publish' : ''}
        ${isLimitedHeightStep ? ' amp-flow--limited-height' : ''}
        ${isPublishStep && isDetailsExpanded ? ' amp-flow--details-expanded' : ''}
        ${isShareStep ? ' amp-flow--share' : ''}`}
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
      >
        <WizardHeader
          hideBack={
            isShareStep ||
            step === 'address' ||
            // Launched from the building view / preview card: the building step
            // is the entry point, so Back would only close the flow — which the
            // close X already does. Hide the redundant back arrow.
            (isBuildingStep && enteredFromBuildingViewRef.current)
          }
          onBack={handleBack}
          onClose={handleCloseFlow}
          activeDot={STEP_DOT[step] ?? null}
          activeDotDone={activeDotDone}
        />
        <div
          className={`amp-flow__content amp-flow__content--${step}`}
          data-address-focused={
            step === 'address' && isAddressFocused ? 'true' : 'false'
          }
        >
          {stepNode}
          {sharedFlowMap}
        </div>
      </div>
      <ImageLightbox
        isOpen={lightboxIndex !== null && images.length > 0}
        images={images}
        initialIndex={lightboxIndex ?? 0}
        onClose={() => setLightboxIndex(null)}
        onToggleVersion={toggleImageVersion}
        onRegenerate={regenerateImage}
        isRegenerating={isRegenerating}
        enhancementCredits={enhancementStatus?.credits ?? null}
        onEnhance={regenerateImage}
        onBuyCredits={handleImageCreditsRequired}
      />
    </div>
  );

  return createPortal(overlay, document.body);
};

export default AddMyPlaceFlow;
